import { useRef, useState, useCallback } from 'react';

/**
 * Custom hook for iOS-style swipe-down to close modals
 * Returns refs and handlers to attach to modal elements
 */
export function useSwipeDown(onClose, threshold = 100) {
  const [isDragging, setIsDragging] = useState(false);
  const [translateY, setTranslateY] = useState(0);
  const startY = useRef(0);
  const currentY = useRef(0);
  const modalRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    // Allow drag from anywhere on the modal
    const touch = e.touches[0];
    const modal = modalRef.current;
    if (!touch || !modal) return;

    // Check if modal is scrollable and if it's at the top
    // If scrollable and not at top, allow normal scrolling instead of swipe-down
    const isScrollable = modal.scrollHeight > modal.clientHeight;
    const isAtTop = modal.scrollTop === 0;
    
    // Only start swipe-down if modal is at top or not scrollable
    // This prevents conflicts with scrolling
    if (isScrollable && !isAtTop) {
      return;
    }

    // Stop CSS animations and transitions when starting to drag
    modal.style.animation = 'none';
    modal.style.transition = 'none';
    
    // Get current transform to maintain translateX if present
    const computedStyle = window.getComputedStyle(modal);
    const currentTransform = computedStyle.transform;
    const hasTranslateX = currentTransform.includes('translateX(-50%)') || 
                          modal.classList.contains('menu-modal') ||
                          modal.classList.contains('score-modal') ||
                          modal.classList.contains('faq-modal');

    startY.current = touch.clientY;
    currentY.current = touch.clientY;
    setIsDragging(true);
    setTranslateY(0);
    
    // Store base transform for later use
    modal.dataset.baseTransform = hasTranslateX ? 'translateX(-50%)' : '';
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    const modal = modalRef.current;
    if (!touch || !modal) return;

    const deltaY = touch.clientY - startY.current;

    // Only allow downward swipes (positive deltaY)
    if (deltaY > 0) {
      // If modal is scrollable and at top, allow swipe-down
      // If modal is scrollable and not at top, check if we're trying to scroll up
      const isScrollable = modal.scrollHeight > modal.clientHeight;
      const isAtTop = modal.scrollTop === 0;
      
      if (isScrollable && !isAtTop && deltaY < 0) {
        // User is trying to scroll up, don't interfere
        return;
      }

      // Prevent default scrolling behavior during swipe-down
      e.preventDefault();
      
      currentY.current = touch.clientY;
      setTranslateY(deltaY);
      
      // Apply transform directly to modal for immediate visual feedback
      const baseTransform = modal.dataset.baseTransform || '';
      const transformValue = baseTransform 
        ? `${baseTransform} translateY(${deltaY}px)`
        : `translateY(${deltaY}px)`;
      modal.style.transform = transformValue;
    }
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;

    const deltaY = currentY.current - startY.current;

    // If swiped down more than threshold, close modal
    if (deltaY > threshold) {
      // Trigger close with slide-down animation
      if (modalRef.current) {
        const modal = modalRef.current;
        const baseTransform = modal.dataset.baseTransform || '';
        
        // Restore transition for smooth close animation
        modal.style.transition = 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)';
        
        // Slide down to close
        const transformValue = baseTransform 
          ? `${baseTransform} translateY(100vh)`
          : 'translateY(100vh)';
        modal.style.transform = transformValue;
        
        // Wait for animation to complete before closing
        setTimeout(() => {
          onClose();
          // Clean up
          if (modal.dataset.baseTransform) {
            delete modal.dataset.baseTransform;
          }
        }, 300);
      }
    } else {
      // Snap back to original position
      if (modalRef.current) {
        const modal = modalRef.current;
        const baseTransform = modal.dataset.baseTransform || '';
        
        // Restore transition for smooth snap-back
        modal.style.transition = 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)';
        
        // Snap back to original position
        const transformValue = baseTransform 
          ? `${baseTransform} translateY(0)`
          : 'translateY(0)';
        modal.style.transform = transformValue;
        
        // Clear the stored base transform after a delay
        setTimeout(() => {
          if (modal.dataset.baseTransform) {
            delete modal.dataset.baseTransform;
          }
        }, 300);
      }
    }

    setIsDragging(false);
    setTranslateY(0);
  }, [isDragging, threshold, onClose]);

  // Apply transform during drag (fallback for initial render)
  const getModalStyle = useCallback(() => {
    // Transform is applied directly in handleTouchMove for better performance
    // This is just a fallback
    if (!isDragging || translateY === 0) return {};
    
    const modal = modalRef.current;
    if (!modal) return {};
    
    const baseTransform = modal.dataset.baseTransform || '';
    const transformValue = baseTransform 
      ? `${baseTransform} translateY(${translateY}px)`
      : `translateY(${translateY}px)`;
    
    return {
      transform: transformValue,
      transition: 'none', // Disable transition during drag for smooth following
    };
  }, [isDragging, translateY]);

  return {
    modalRef,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    getModalStyle,
    isDragging,
  };
}

